import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/feature/BreadcrumbNav';
import CategoryTabs from './components/CategoryTabs';
import ArticleCard from './components/ArticleCard';
import { categories, allArticles, categoriesMap } from '@/mocks/categoryData';
import type { ArticleItem } from '@/mocks/categoryData';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentSlug = slug || 'hot';
  const [activeSlug, setActiveSlug] = useState(currentSlug);


  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const categoryName = activeCategory ? activeCategory.name : t('category.all');
  useEffect(() => {
    document.title = t('category.pageTitle');
  }, [t]);

  useEffect(() => {
    setActiveSlug(currentSlug);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentSlug]);

  useEffect(() => {
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'breadcrumb-schema';

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': t('breadcrumb.home'),
          'item': 'http://zwrn.cn/',
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': t('breadcrumb.category'),
          'item': 'http://zwrn.cn/category/hot',
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'name': categoryName,
        },
      ],
    };
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);

    const existing = document.getElementById('breadcrumb-schema');
    if (existing) existing.remove();
    document.head.appendChild(breadcrumbScript);

    const collectionScript = document.createElement('script');
    collectionScript.type = 'application/ld+json';
    collectionScript.id = 'collection-schema';

    const collectionSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': `${categoryName} - 51吃瓜网`,
      'description': `51吃瓜网${categoryName}分类页面，浏览最新最全的${categoryName}内容。`,
      'url': 'http://zwrn.cn/',
      'isPartOf': {
        '@type': 'WebSite',
        'name': '51吃瓜网',
        'url': 'http://zwrn.cn/',
      },
    };
    collectionScript.textContent = JSON.stringify(collectionSchema);

    const existingCol = document.getElementById('collection-schema');
    if (existingCol) existingCol.remove();
    document.head.appendChild(collectionScript);

    return () => {
      const b = document.getElementById('breadcrumb-schema');
      const c = document.getElementById('collection-schema');
      if (b) b.remove();
      if (c) c.remove();
    };
  }, [categoryName, activeSlug, t]);

  const handleSelectCategory = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate('/', { replace: true });
  };

  const articles: ArticleItem[] = useMemo(() => {
    if (activeSlug === 'hot') {
      return [...allArticles].sort((a, b) => b.views - a.views);
    }
    return categoriesMap[activeSlug] || Object.values(categoriesMap).flat();
  }, [activeSlug]);

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), href: '/' },
    { label: t('breadcrumb.category'), href: '/category/hot' },
    { label: categoryName },
  ];

  return (
    <>
      <main>
        <Navbar />
        <div className="pt-24 md:pt-28 pb-16 md:pb-24 bg-background-50">
          <BreadcrumbNav items={breadcrumbItems} />

          <div className="w-full px-6 md:px-10 lg:px-16 mt-4">
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-950 mb-8">
              {t('category.heading')}
            </h1>

            <CategoryTabs activeSlug={activeSlug} onSelect={handleSelectCategory} />

            <div className="mt-8">
              {articles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {articles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                  <p className="text-foreground-500 text-sm">{t('category.noArticles')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}